import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Box, Button, Icon, Spinner, Stack, Text, useBreakpointValue } from "@chakra-ui/react";
import { MdCardMembership, MdAdd, MdReceiptLong } from "react-icons/md";
import SubscriptionsTable from "./SubscriptionsTable";
import SubscriptionDetailDrawer from "./SubscriptionDetailDrawer";
import CreateSubscriptionDialog from "./CreateSubscriptionDialog";
import UserInvoicesDrawer from "./UserInvoicesDrawer";

const primaryButtonProps = {
    background: "rgba(23, 220, 184, 0.15)",
    color: "#17dcb8",
    border: "1px solid rgba(23, 220, 184, 0.4)",
    fontWeight: 600,
    _hover: { background: "rgba(23, 220, 184, 0.25)" },
    _disabled: { opacity: 0.5 },
};

const secondaryButtonProps = {
    color: "#17dcb8",
    borderColor: "rgba(23, 220, 184, 0.4)",
    fontWeight: 600,
    _hover: { borderColor: "#17dcb8", background: "rgba(23, 220, 184, 0.08)" },
};

const headerContainerProps = (isMobile) => ({
    mb: 4,
    padding: isMobile ? "10px 12px" : "12px 14px",
    background: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(10px)",
    borderRadius: "10px",
    border: "1px solid rgba(255, 255, 255, 0.12)",
});

const iconBadgeProps = (isMobile) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: isMobile ? "28px" : "32px",
    height: isMobile ? "28px" : "32px",
    borderRadius: "8px",
    background: "rgba(23, 220, 184, 0.15)",
    border: "1px solid rgba(23, 220, 184, 0.3)",
    boxShadow: "0 2px 8px rgba(23, 220, 184, 0.2)",
});

const stateBoxProps = (isMobile) => ({
    textAlign: "center",
    padding: isMobile ? "16px" : "24px",
    background: "rgba(255, 255, 255, 0.02)",
    borderRadius: "8px",
    border: "1px solid rgba(255, 255, 255, 0.05)",
});

/**
 * Normalizes the API response into an array of subscription objects.
 * Accepts either a raw array or { subscriptions: [...] } / { items: [...] }.
 * @param {*} data - Decrypted API payload
 * @returns {Array}
 */
function normalizeSubscriptions(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.subscriptions)) return data.subscriptions;
    if (Array.isArray(data?.items)) return data.items;
    return [];
}

/**
 * Fetches subscriptions for a user owner.
 * Cancellation flag prevents state updates after unmount or userId change.
 * The returned `refresh` increments an internal tick to re-trigger the fetch.
 * @param {number} userId - Target user ID
 * @returns {{ subscriptions: Array, loading: boolean, error: string|null, refresh: Function }}
 */
function useUserSubscriptions(userId) {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);

    const fetchSubs = useCallback(async (id, signal) => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(`/admin/subscriptions/owner/user/${id}`);
            if (!signal.aborted) setSubscriptions(normalizeSubscriptions(data));
        } catch (err) {
            if (!signal.aborted) setError(err?.response?.data || "Impossible de charger les abonnements");
        } finally {
            if (!signal.aborted) setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!userId) { setSubscriptions([]); return undefined; }
        const controller = new AbortController();
        fetchSubs(userId, controller.signal);
        return () => controller.abort();
    }, [userId, tick, fetchSubs]);

    return { subscriptions, loading, error, refresh: () => setTick((t) => t + 1) };
}

/**
 * Renders the title block (icon badge + label) of the subscriptions header.
 * @param {Object} props
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function HeaderTitle({ isMobile }) {
    return (
        <Stack direction="row" spacing={2} alignItems="center" minW={0}>
            <Box {...iconBadgeProps(isMobile)}>
                <Icon as={MdCardMembership} color="#17dcb8" boxSize={isMobile ? "16px" : "18px"} />
            </Box>
            <Text noOfLines={1} color="rgba(255, 255, 255, 0.95)" fontSize={isMobile ? "0.85rem" : "0.9rem"} fontWeight={600} letterSpacing="0.3px">
                Abonnements
            </Text>
        </Stack>
    );
}

/**
 * Renders the two header CTAs (billing history drawer + new subscription dialog).
 * @param {Object} props
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @param {Function} props.onCreateClick - "Nouvel abonnement" click handler
 * @param {Function} props.onHistoryClick - "Historique factures" click handler
 * @returns {React.ReactElement}
 */
function HeaderActions({ isMobile, onCreateClick, onHistoryClick }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="center">
            <Button onClick={onHistoryClick} size="sm" variant="outline" {...secondaryButtonProps}
                leftIcon={!isMobile ? <Icon as={MdReceiptLong} /> : undefined}>
                {isMobile ? <Icon as={MdReceiptLong} boxSize="16px" /> : "Historique factures"}
            </Button>
            <Button onClick={onCreateClick} size="sm" {...primaryButtonProps}
                leftIcon={!isMobile ? <Icon as={MdAdd} /> : undefined}>
                {isMobile ? <Icon as={MdAdd} boxSize="16px" /> : "Nouvel abonnement"}
            </Button>
        </Stack>
    );
}

/**
 * Renders the subscriptions header with icon, title and the two CTAs.
 * @param {Object} props
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @param {Function} props.onCreateClick - "Nouvel abonnement" click handler
 * @param {Function} props.onHistoryClick - "Historique factures" click handler
 * @returns {React.ReactElement}
 */
function SubscriptionsHeader({ isMobile, onCreateClick, onHistoryClick }) {
    return (
        <Box {...headerContainerProps(isMobile)}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} spacing={2}>
                <HeaderTitle isMobile={isMobile} />
                <HeaderActions isMobile={isMobile} onCreateClick={onCreateClick} onHistoryClick={onHistoryClick} />
            </Stack>
            <Text color="rgba(255, 255, 255, 0.6)" fontSize={isMobile ? "0.65rem" : "0.7rem"} pl={isMobile ? "36px" : "40px"}>
                Tous les abonnements liés à cet utilisateur
            </Text>
        </Box>
    );
}

/**
 * Renders a centered loading spinner.
 * @param {Object} props
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function LoadingState({ isMobile }) {
    return (
        <Box {...stateBoxProps(isMobile)}>
            <Spinner size="lg" color="#17dcb8" />
        </Box>
    );
}

/**
 * Renders an error message in the same styled container as empty state.
 * @param {Object} props
 * @param {string} props.message - Error message to display
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function ErrorState({ message, isMobile }) {
    return (
        <Box {...stateBoxProps(isMobile)}>
            <Text color="rgba(229, 115, 115, 1)" fontSize={isMobile ? "0.75rem" : "0.8rem"}>
                {typeof message === "string" ? message : "Erreur lors du chargement"}
            </Text>
        </Box>
    );
}

/**
 * Picks the right body component based on loading, error, and data state.
 * @param {Object} props
 * @param {boolean} props.loading - Loading flag
 * @param {string|null} props.error - Error message or null
 * @param {Array} props.subscriptions - List of subscriptions
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @param {Function} props.onSelect - Row click handler (receives subscription id)
 * @returns {React.ReactElement}
 */
function SubscriptionsBody({ loading, error, subscriptions, isMobile, onSelect }) {
    if (loading) return <LoadingState isMobile={isMobile} />;
    if (error) return <ErrorState message={error} isMobile={isMobile} />;
    return <SubscriptionsTable subscriptions={subscriptions} isMobile={isMobile} onSelect={onSelect} />;
}

/**
 * Subscriptions tab for a user profile (owner_type=user).
 * Lists all subscriptions tied to the user and opens a detail drawer on row click.
 * @param {Object} props
 * @param {number} props.userId - Target user ID
 * @returns {React.ReactElement}
 */
function Subscriptions({ userId }) {
    const isMobile = useBreakpointValue({ base: true, sm: false });
    const { subscriptions, loading, error, refresh } = useUserSubscriptions(userId);
    const [selectedId, setSelectedId] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);

    return (
        <Box>
            <SubscriptionsHeader isMobile={isMobile}
                onCreateClick={() => setCreateOpen(true)}
                onHistoryClick={() => setHistoryOpen(true)} />
            <SubscriptionsBody loading={loading} error={error} subscriptions={subscriptions}
                isMobile={isMobile} onSelect={setSelectedId} />
            <SubscriptionDetailDrawer subscriptionId={selectedId}
                onClose={() => setSelectedId(null)} onChanged={refresh} />
            <CreateSubscriptionDialog open={createOpen} onClose={() => setCreateOpen(false)}
                userId={userId} onCreated={refresh} />
            <UserInvoicesDrawer open={historyOpen} userId={userId}
                onClose={() => setHistoryOpen(false)} />
        </Box>
    );
}

export default Subscriptions;
